import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Heart, MessageCircle, ChevronRight, Clock, Share2 } from "lucide-react";
import api from "../lib/api";
import { toast } from "sonner";
import BottomNav from "../components/BottomNav";
import MatchModal from "../components/MatchModal";
import PremiumBadge from "../components/PremiumBadge";
import { useAuth } from "../lib/auth";
import { tgHapticNotification } from "../lib/telegram";
import { t } from "../lib/i18n";

function formatTimeAgo(iso, lang) {
  if (!iso) return null;
  const now = Date.now();
  const then = new Date(iso).getTime();
  const diff = now - then;
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return lang === "ru" ? "только что" : "just now";
  if (mins < 60) return `${mins} ${lang === "ru" ? "мин" : "min"}`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours} ${lang === "ru" ? "ч" : "h"}`;
  const days = Math.floor(hours / 24);
  return `${days} ${lang === "ru" ? "д" : "d"}`;
}

function getLastMessagePreview(msg, myId, lang) {
  if (!msg) return null;
  if (msg.kind === "telegram_share") {
    return {
      icon: <Share2 className="w-3 h-3" />,
      text: lang === "ru" ? "Отправлен Telegram" : "Telegram sent",
      fromMe: msg.from_user_id === myId,
    };
  }
  const txt = msg.text || "";
  const truncated = txt.length > 50 ? txt.slice(0, 50) + "…" : txt;
  return {
    icon: null,
    text: truncated,
    fromMe: msg.from_user_id === myId,
  };
}

export default function Matches() {
  const nav = useNavigate();
  const { user } = useAuth();
  const lang = user?.language || "ru";
  const myId = user?.id;
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [subTab, setSubTab] = useState("matches");
  const [likes, setLikes] = useState([]);
  const [likesLoading, setLikesLoading] = useState(false);
  const [likesLoaded, setLikesLoaded] = useState(false);
  const [likingId, setLikingId] = useState(null);
  const [matchModal, setMatchModal] = useState(null);
  const [lastMatchId, setLastMatchId] = useState(null);

  useEffect(() => {
    api.get("/matches").then(({ data }) => {
      setItems(data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (subTab !== "likes" || likesLoaded || !user?.is_premium) return;
    setLikesLoading(true);
    api.get("/premium/likes-received")
      .then(({ data }) => setLikes(data))
      .catch(() => {})
      .finally(() => { setLikesLoading(false); setLikesLoaded(true); });
  }, [subTab, likesLoaded, user?.is_premium]);

  const sorted = useMemo(() => {
    return [...items].sort((a, b) => {
      // Online/active first, then by last_message_at
      const aTime = a.last_message_at ? new Date(a.last_message_at).getTime() : 0;
      const bTime = b.last_message_at ? new Date(b.last_message_at).getTime() : 0;
      return bTime - aTime;
    });
  }, [items]);

  const likeBack = async (likeItem) => {
    if (likingId) return;
    setLikingId(likeItem.like_id);
    try {
      const { data } = await api.post("/likes", { to_user_id: likeItem.user.id, type: "like" });
      if (data.matched) {
        tgHapticNotification("success");
        setMatchModal(data.matched_user);
        setLastMatchId(data.match?.id);
      }
      setLikes((curr) => curr.filter((l) => l.like_id !== likeItem.like_id));
    } catch (e) {
      toast.error(e.response?.data?.detail || t(lang, "error_generic"));
    } finally {
      setLikingId(null);
    }
  };

  const openChat = () => {
    if (lastMatchId) {
      setMatchModal(null);
      nav(`/chat/${lastMatchId}`);
    }
  };

  return (
    <div className="kupidon-shell safe-top flex flex-col relative" data-testid="matches-page">
      {/* Decorative header with gradient */}
      <div className="absolute top-0 left-0 right-0 h-48 bg-gradient-to-b from-[color:var(--peach)]/10 to-transparent pointer-events-none" />

      <div className="px-5 pt-4 pb-3 relative z-10">
        <div className="text-[11px] uppercase tracking-[0.25em] text-[color:var(--ink-soft)] font-medium">
          {t(lang, "matches_kicker")}
        </div>
        <div className="font-[Onest] font-extrabold text-3xl -mt-0.5 tracking-tight">
          {t(lang, "matches_title")}
        </div>
      </div>

      {user?.is_premium && (
        <div className="px-5 pb-2 relative z-10">
          <div className="flex gap-1.5 p-1 rounded-2xl bg-white/70 backdrop-blur border border-[color:var(--border)]">
            {[
              { id: "matches", label: t(lang, "matches_tab_matches") },
              { id: "likes", label: t(lang, "matches_tab_likes") },
            ].map((st) => (
              <button
                key={st.id}
                onClick={() => setSubTab(st.id)}
                data-testid={`matches-subtab-${st.id}`}
                className={`flex-1 py-2 rounded-xl text-xs font-semibold transition-all ${
                  subTab === st.id
                    ? "bg-[color:var(--ink)] text-white shadow-sm"
                    : "text-[color:var(--ink-soft)] hover:text-[color:var(--ink)]"
                }`}
              >
                {st.label}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="flex-1 overflow-y-auto no-scrollbar px-4 pb-28 relative z-10">
        {subTab === "likes" && user?.is_premium ? (
          <>
            {likesLoading && (
              <div className="flex flex-col items-center justify-center py-20 gap-3">
                <div className="w-10 h-10 rounded-full border-2 border-[color:var(--peach)] border-t-transparent animate-spin" />
                <div className="text-sm text-[color:var(--ink-soft)]">{t(lang, "loading")}</div>
              </div>
            )}
            {!likesLoading && likes.length === 0 && (
              <div className="flex flex-col items-center justify-center text-center pt-24 px-6">
                <div className="w-28 h-28 rounded-full bg-gradient-to-br from-[color:var(--stars-gold)]/25 to-[color:var(--peach-deep)]/10 flex items-center justify-center mb-5 shadow-inner">
                  <Heart className="w-12 h-12 text-[color:var(--peach-deep)]" />
                </div>
                <div className="text-sm text-[color:var(--ink-soft)] max-w-[28ch] leading-relaxed">
                  {t(lang, "likes_received_empty")}
                </div>
              </div>
            )}
            <div className="space-y-3 mt-2">
              {likes.map((item, idx) => {
                const photo = item.user.photos?.[0]?.data_url;
                const blurred = item.user.photos?.[0]?.blurred;
                return (
                  <motion.div
                    key={item.like_id}
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: idx * 0.05, ease: [0.16, 1, 0.3, 1] }}
                    className="w-full flex items-center gap-4 p-3 rounded-2xl bg-white/70 backdrop-blur-sm border border-white/40 shadow-[0_2px_12px_rgba(244,162,97,0.08)]"
                  >
                    <button
                      onClick={() => nav(`/person/${item.user.id}`, { state: { profile: item.user, backTo: "/matches" } })}
                      className="relative w-16 h-16 rounded-2xl overflow-hidden bg-[color:var(--peach)]/15 flex-shrink-0 ring-2 ring-white/50"
                    >
                      {photo ? (
                        <img src={photo} alt="" className={`w-full h-full object-cover ${blurred ? "photo-blur" : ""}`} />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-[color:var(--peach-deep)]">
                          <Heart className="w-6 h-6" />
                        </div>
                      )}
                    </button>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 min-w-0">
                        <div className="font-[Onest] font-bold text-lg tracking-tight truncate">
                          {item.user.first_name}
                        </div>
                        <div className="text-[color:var(--ink-soft)] text-sm font-medium flex-shrink-0">
                          {item.user.age}
                        </div>
                        {item.user.is_premium && <PremiumBadge variant="pill" />}
                      </div>
                      <div className="text-[10px] text-[color:var(--ink-soft)] flex items-center gap-1 mt-0.5">
                        <Clock className="w-3 h-3" />
                        {formatTimeAgo(item.created_at, lang)}
                      </div>
                    </div>
                    <button
                      onClick={() => likeBack(item)}
                      disabled={likingId === item.like_id}
                      data-testid={`like-back-${item.like_id}`}
                      className="px-3 py-2 rounded-xl bg-[color:var(--peach)] text-white text-xs font-semibold flex items-center gap-1.5 flex-shrink-0 disabled:opacity-50"
                    >
                      {likingId === item.like_id ? (
                        <div className="w-3.5 h-3.5 rounded-full border-2 border-white border-t-transparent animate-spin" />
                      ) : (
                        <><Heart className="w-3.5 h-3.5" /> {t(lang, "likes_received_like_back")}</>
                      )}
                    </button>
                  </motion.div>
                );
              })}
            </div>
          </>
        ) : (
          <>
            {loading && (
              <div className="flex flex-col items-center justify-center py-20 gap-3">
                <div className="w-10 h-10 rounded-full border-2 border-[color:var(--peach)] border-t-transparent animate-spin" />
                <div className="text-sm text-[color:var(--ink-soft)]">{t(lang, "loading")}</div>
              </div>
            )}

            {!loading && items.length === 0 && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="flex flex-col items-center justify-center text-center pt-24 px-6"
              >
                <div className="w-28 h-28 rounded-full bg-gradient-to-br from-[color:var(--peach)]/20 to-[color:var(--peach-deep)]/10 flex items-center justify-center mb-5 shadow-inner">
                  <Heart className="w-12 h-12 text-[color:var(--peach-deep)]" />
                </div>
                <div className="font-[Onest] font-bold text-2xl tracking-tight">{t(lang, "matches_empty_title")}</div>
                <div className="text-sm text-[color:var(--ink-soft)] mt-3 max-w-[28ch] leading-relaxed">
                  {t(lang, "matches_empty_sub")}
                </div>
              </motion.div>
            )}

            <div className="space-y-3 mt-2">
              {sorted.map((m, idx) => {
            const photo = m.user.photos?.[0]?.data_url;
            const blurred = m.user.photos?.[0]?.blurred;
            const preview = getLastMessagePreview(m.last_message, myId, lang);
            const timeAgo = formatTimeAgo(m.last_message_at, lang);

            return (
              <motion.button
                key={m.match_id}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.05, ease: [0.16, 1, 0.3, 1] }}
                onClick={() => nav(`/chat/${m.match_id}`)}
                data-testid={`match-item-${m.match_id}`}
                className="w-full flex items-center gap-4 p-3 rounded-2xl bg-white/70 backdrop-blur-sm border border-white/40 hover:bg-white/90 active:scale-[0.98] transition-all duration-200 text-left shadow-[0_2px_12px_rgba(244,162,97,0.08)]"
              >
                {/* Avatar — badge lives in an outer wrapper WITHOUT overflow-hidden,
                    since it needs to peek outside the 64px box via negative offsets;
                    overflow-hidden only clips the inner photo layer. */}
                <div className="relative w-16 h-16 flex-shrink-0">
                  <div className="w-full h-full rounded-2xl overflow-hidden bg-[color:var(--peach)]/15 ring-2 ring-white/50">
                    {photo ? (
                      <img
                        src={photo}
                        alt=""
                        className={`w-full h-full object-cover ${blurred ? "photo-blur" : ""}`}
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-[color:var(--peach-deep)]">
                        <Heart className="w-6 h-6" />
                      </div>
                    )}
                  </div>
                  {/* Unread badge — prominent, positioned outside avatar */}
                  {m.unread > 0 && (
                    <div className="absolute -top-1.5 -right-1.5 min-w-[24px] h-6 px-1.5 rounded-full bg-gradient-to-r from-[color:var(--peach-deep)] to-[color:var(--peach)] text-white text-[11px] font-bold flex items-center justify-center shadow-[0_2px_6px_rgba(231,111,81,0.4)] ring-2 ring-white z-10">
                      {m.unread > 99 ? "99+" : m.unread}
                    </div>
                  )}
                  {/* Online dot indicator */}
                  <div className="absolute bottom-0.5 right-0.5 w-3 h-3 rounded-full bg-[color:var(--teal-superlike)] border-2 border-white shadow-sm" />
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2 min-w-0">
                      <div className="font-[Onest] font-bold text-lg tracking-tight truncate">
                        {m.user.first_name}
                      </div>
                      <div className="text-[color:var(--ink-soft)] text-sm font-medium flex-shrink-0">
                        {m.user.age}
                      </div>
                      {m.user.is_premium && <PremiumBadge variant="pill" />}
                    </div>
                    {timeAgo && (
                      <div className="text-[10px] text-[color:var(--ink-soft)] flex items-center gap-1 flex-shrink-0">
                        <Clock className="w-3 h-3" />
                        {timeAgo}
                      </div>
                    )}
                  </div>

                  {/* Last message preview */}
                  <div className="flex items-center gap-1.5 mt-0.5">
                    {preview ? (
                      <>
                        {preview.fromMe && (
                          <span className="text-[10px] text-[color:var(--ink-soft)] flex-shrink-0">
                            {lang === "ru" ? "Вы:" : "You:"}
                          </span>
                        )}
                        {preview.icon && (
                          <span className="text-[color:var(--peach-deep)] flex-shrink-0">
                            {preview.icon}
                          </span>
                        )}
                        <span className={`text-xs truncate ${m.unread > 0 ? "font-semibold text-[color:var(--ink)]" : "text-[color:var(--ink-soft)]"}`}>
                          {preview.text}
                        </span>
                      </>
                    ) : (
                      <span className="text-xs text-[color:var(--peach-deep)] font-semibold flex items-center gap-1">
                        <MessageCircle className="w-3 h-3" /> {t(lang, "say_hi")}
                      </span>
                    )}
                  </div>
                </div>

                {/* Arrow */}
                <div className="w-7 h-7 rounded-full bg-[color:var(--peach)]/10 flex items-center justify-center flex-shrink-0">
                  <ChevronRight className="w-4 h-4 text-[color:var(--peach-deep)]" />
                </div>
              </motion.button>
            );
          })}
            </div>
          </>
        )}
      </div>

      <BottomNav />

      <MatchModal
        open={!!matchModal}
        matched={matchModal}
        myFirstName={user?.first_name}
        onSendMessage={openChat}
        onKeepSwiping={() => setMatchModal(null)}
      />
    </div>
  );
}
